import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, ConfigProvider, Flex, Modal, Segmented, Space, Spin, Typography, theme } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ConfigCard } from './ConfigCard';
import {
  insuranceCarrierConfigurationsQueryKeys,
  useGetInsuranceCarrierConfigurations,
  useCreateInsuranceCarrierConfiguration,
  useUpdateInsuranceCarrierConfiguration,
} from '@features/insuranceCarriers/hooks/useInsuranceCarrierConfigurations';
import { insuranceCarriersQueryKeys } from '@features/insuranceCarriers/hooks/useInsuranceCarriers';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { CONNECTION_TYPES } from '@shared/constants/connectionTypes';

const { Text } = Typography;

const TAB_LABELS = {
  [CONNECTION_TYPES.API]: 'Configuración para API',
  [CONNECTION_TYPES.RPA]: 'Configuración para Bot',
};

const TAB_DESCRIPTIONS = {
  [CONNECTION_TYPES.API]: 'Estas credenciales se usarán para la conexión directa mediante API con la aseguradora.',
  [CONNECTION_TYPES.RPA]: 'Estas credenciales se usarán para la conexión mediante bot automatizado con la aseguradora.',
};

export const CredentialsFormModal = ({ record, open, onCancel, readOnly }) => {
  const configurableConnectionTypes = useMemo(
    () => record.connectionTypes.filter((ct) => ct.credentialsSchema?.fields?.length > 0),
    [record.connectionTypes],
  );

  const [activeTab, setActiveTab] = useState(() => configurableConnectionTypes[0]?.code ?? CONNECTION_TYPES.API);
  const [extraCards, setExtraCards] = useState([]);
  const [defaultSedeId, setDefaultSedeId] = useState('__general__');
  const [enabledByTab, setEnabledByTab] = useState({});
  const mainCardRef = useRef(null);
  const extraCardRefs = useRef({});
  const enabledInitialized = useRef(false);
  const sedeInitialized = useRef(false);

  const { notification } = App.useApp();
  const { token: themeToken } = theme.useToken();
  const queryClient = useQueryClient();

  const branches = useSelector((state) => state.initialData.sedes);
  const branchOptions = branches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  }));

  const { data: allConfigurations, isLoading, isFetching } = useGetInsuranceCarrierConfigurations(
    { insuranceCarrierId: record.id, filters: {} },
    { enabled: open },
  );

  // Filtrar por tab activo para la card principal
  const existingConfigurations = useMemo(
    () => allConfigurations?.filter((c) => c.connectionType.code === activeTab),
    [allConfigurations, activeTab],
  );

  const createMutation = useCreateInsuranceCarrierConfiguration();
  const updateMutation = useUpdateInsuranceCarrierConfiguration();

  const isMutating = createMutation.isPending || updateMutation.isPending;

  // Al cambiar de tab, limpiar cards extra y reiniciar sede detectada
  useEffect(() => {
    setExtraCards([]);
    extraCardRefs.current = {};
    sedeInitialized.current = false;
    setDefaultSedeId('__general__');
  }, [activeTab]);

  // Al cerrar el modal, reiniciar todo para la próxima apertura
  useEffect(() => {
    if (!open) {
      sedeInitialized.current = false;
      enabledInitialized.current = false;
      setDefaultSedeId('__general__');
      setEnabledByTab({});
    }
  }, [open]);

  // Si el admin habilitó ambas desde Django, ambas se muestran activas — la exclusión mutua solo aplica al hacer clic en el switch.
  useEffect(() => {
    if (!open || !allConfigurations || isFetching || enabledInitialized.current) return;
    enabledInitialized.current = true;

    const initial = {};
    configurableConnectionTypes.forEach((ct) => {
      const configs = allConfigurations.filter((c) => c.connectionType.code === ct.code);
      initial[ct.code] = configs.some((c) => c.sede === null && c.enabled);
    });

    setEnabledByTab(initial);
  }, [open, allConfigurations, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detectar la sede activa: la config guardada más recientemente es la que está en uso
  useEffect(() => {
    if (!open || !existingConfigurations || isFetching || sedeInitialized.current) return;
    sedeInitialized.current = true;
    // Forzar SIEMPRE a General por instrucción del usuario
    setDefaultSedeId('__general__');
  }, [open, existingConfigurations, isFetching]);

  // Exclusión mutua: activar un tipo de conexión desactiva los demás
  const handleConnectionTypeEnabledChange = (checked) => {
    setEnabledByTab((prev) => {
      if (checked) {
        const next = {};
        configurableConnectionTypes.forEach((ct) => {
          next[ct.code] = ct.code === activeTab;
        });
        return next;
      }
      return { ...prev, [activeTab]: false };
    });
  };

  const activeConnectionType = configurableConnectionTypes.find((ct) => ct.code === activeTab);
  const credentialsSchema = activeConnectionType?.credentialsSchema;

  const fullSchema = useMemo(() => {
    if (!credentialsSchema) return null;
    return {
      ...credentialsSchema,
      fields: [
        { key: 'sede', label: 'Sedes', type: 'select', required: true, options: [{ value: '__general__', label: 'General', order: 1 }, ...branchOptions] },
        ...credentialsSchema.fields,
      ],
    };
  }, [credentialsSchema, branchOptions]);

  const newSedeSchema = useMemo(() => {
    if (!credentialsSchema) return null;
    return {
      ...credentialsSchema,
      fields: [
        { key: 'sedeName', label: 'Nueva sede', type: 'string', required: true },
        ...credentialsSchema.fields,
      ],
    };
  }, [credentialsSchema]);

  const handleAddCard = () => {
    setExtraCards((prev) => [...prev, { key: `new-${Date.now()}` }]);
  };

  const handleRemoveCard = (key) => {
    setExtraCards((prev) => prev.filter((c) => c.key !== key));
    delete extraCardRefs.current[key];
  };

  const handleSave = async () => {
    try {
      const allRefs = [
        mainCardRef.current,
        ...extraCards.map((c) => extraCardRefs.current[c.key]),
      ].filter(Boolean);

      const results = await Promise.all(allRefs.map((r) => r.getValues()));

      await Promise.all(
        results.map(({ branchId, sedeName, credentials, enabled, existingId }) => {
          if (existingId) {
            return updateMutation.mutateAsync({
              insuranceCarrierId: record.id,
              configurationId: existingId,
              data: { credentials, enabled },
            });
          }
          return createMutation.mutateAsync({
            insuranceCarrierId: record.id,
            data: {
              connectionTypeCode: activeTab,
              credentials,
              enabled,
              ...(sedeName ? { sedeName } : { branchId }),
            },
          });
        }),
      );

      // Sincronizar el estado habilitado/deshabilitado a TODAS las demás configs
      // (otras sedes del tab activo + todas las configs de otros tipos de conexión)
      const updatedConfigIds = new Set(results.map((r) => r.existingId).filter(Boolean));
      const syncPromises = [];
      for (const ct of configurableConnectionTypes) {
        const shouldBeEnabled = enabledByTab[ct.code] ?? false;
        const ctConfigs = allConfigurations?.filter((c) => c.connectionType.code === ct.code) ?? [];
        for (const c of ctConfigs) {
          if (updatedConfigIds.has(c.id)) continue;
          if (c.enabled !== shouldBeEnabled) {
            syncPromises.push(
              updateMutation.mutateAsync({
                insuranceCarrierId: record.id,
                configurationId: c.id,
                data: { enabled: shouldBeEnabled },
              }),
            );
          }
        }
      }
      await Promise.all(syncPromises);

      void queryClient.invalidateQueries({ queryKey: insuranceCarriersQueryKeys.list() });
      void queryClient.invalidateQueries({ queryKey: insuranceCarrierConfigurationsQueryKeys.base() });

      notification.success({
        message: <Text strong>Configuración de <Text italic>{record.name}</Text> actualizada</Text>,
        description: '¡La configuración se ha guardado correctamente!',
      });
      onCancel();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: <Text strong>Error al guardar la configuración</Text>,
        description: getErrorMessage(error),
      });
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const hasConfigurableTypes = configurableConnectionTypes.length > 0;

  const title = (
    <Space align='start'>
      <FontAwesomeIcon icon={faGear} color={themeToken.colorPrimary} style={{ marginTop: 6, fontSize: 20 }} />
      <span style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>Configuración de {record.name}</span>
    </Space>
  );

  const segmentedOptions = configurableConnectionTypes.map((connectionType) => ({
    value: connectionType.code,
    label: TAB_LABELS[connectionType.code] ?? connectionType.name,
  }));

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnHidden
      width={560}
      style={{ borderRadius: 16 }}
      styles={{
        content: { borderRadius: 16, padding: '28px 28px 20px' },
        header: { paddingBottom: 16 },
        body: { paddingTop: 0, paddingInline: 0 },
        footer: { paddingInline: 0, paddingTop: 16, paddingBottom: 0 },
      }}
      footer={
        <Flex justify='flex-end' gap={10}>
          <Button
            onClick={handleCancel}
            style={{ borderRadius: 8, fontWeight: 500, height: 36 }}
          >
            Cancelar
          </Button>
          {!readOnly && hasConfigurableTypes && (
            <Button
              type='primary'
              onClick={handleSave}
              loading={isMutating}
              style={{ borderRadius: 8, fontWeight: 500, height: 36 }}
            >
              Guardar configuración
            </Button>
          )}
        </Flex>
      }
    >
      {hasConfigurableTypes ? (
        <Spin spinning={isLoading || isMutating}>
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  trackBg: '#f0f0f0',
                  trackPadding: 4,
                  itemSelectedBg: '#ffffff',
                  itemSelectedColor: '#262626',
                  itemColor: '#262626',
                  borderRadius: 999,
                  borderRadiusSM: 999,
                  itemHoverColor: '#262626',
                },
                Input: {
                  colorBgContainer: '#f3f3f5',
                  colorBorder: '#f3f3f5',
                  colorText: '#262626',
                  colorTextPlaceholder: '#bfbfbf',
                  borderRadius: 10,
                  controlHeight: 38,
                  hoverBorderColor: '#d9d9d9',
                  activeBorderColor: '#1677ff',
                  activeShadow: 'none',
                },
                Select: {
                  colorBgContainer: '#f3f3f5',
                  colorBorder: '#f3f3f5',
                  colorText: '#262626',
                  colorTextPlaceholder: '#bfbfbf',
                  borderRadius: 10,
                  controlHeight: 38,
                  optionSelectedBg: '#e6f4ff',
                  optionSelectedFontWeight: 700,
                  optionFontSize: 14,
                  selectorBg: '#f3f3f5',
                },
                InputNumber: {
                  colorBgContainer: '#f3f3f5',
                  colorBorder: '#f3f3f5',
                  colorText: '#262626',
                  borderRadius: 10,
                  controlHeight: 38,
                  hoverBorderColor: '#d9d9d9',
                  activeBorderColor: '#1677ff',
                  activeShadow: 'none',
                },
                DatePicker: {
                  colorBgContainer: '#f3f3f5',
                  colorBorder: '#f3f3f5',
                  colorText: '#262626',
                  borderRadius: 10,
                  controlHeight: 38,
                },
                Form: {
                  labelFontSize: 13,
                  labelColor: '#262626',
                  labelRequiredMarkColor: '#ff4d4f',
                  verticalLabelPadding: '0 0 4px',
                },
              },
            }}
          >
            {segmentedOptions.length > 0 && (
              <>
                <Segmented
                  value={activeTab}
                  onChange={setActiveTab}
                  options={segmentedOptions.map((opt) => ({
                    ...opt,
                    label: <Text strong>{opt.label}</Text>,
                  }))}
                  size='large'
                  block
                  style={{ borderRadius: 999, padding: 4 }}
                />
                <Text type='secondary' style={{ display: 'block', marginTop: 14, marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
                  {TAB_DESCRIPTIONS[activeTab]}
                </Text>
              </>
            )}

            <Flex vertical gap={12}>
              {/* Card principal: carga la config existente según la sede que se escoja */}
              <ConfigCard
                ref={mainCardRef}
                index={0}
                fullSchema={fullSchema}
                existingConfigurations={existingConfigurations ?? []}
                defaultSedeId={defaultSedeId}
                readOnly={readOnly}
                externalEnabled={enabledByTab[activeTab] ?? false}
                onEnabledChange={handleConnectionTypeEnabledChange}
                onSedeSelect={setDefaultSedeId}
              />

              {/* Cards adicionales: siempre nuevas, sin config existente */}
              {extraCards.map((card, i) => (
                <ConfigCard
                  key={card.key}
                  ref={(el) => { extraCardRefs.current[card.key] = el; }}
                  index={i + 1}
                  fullSchema={newSedeSchema}
                  existingConfigurations={[]}
                  defaultSedeId={null}
                  readOnly={readOnly}
                  isNewSede
                  showSwitch={false}
                  onRemove={() => handleRemoveCard(card.key)}
                />
              ))}
            </Flex>

            {/*  {!readOnly && (
              <Button
                type='dashed'
                block
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleAddCard}
                style={{ marginTop: 14, borderRadius: 12, height: 40, color: '#595959', borderColor: '#d9d9d9' }}
              >
                Añadir nueva configuración
              </Button>
            )}  */}
          </ConfigProvider>
        </Spin>
      ) : (
        <Alert
          message='Formulario no disponible'
          description='Esta aseguradora aún no tiene un esquema de autenticación configurado.'
          type='info'
          showIcon
        />
      )}
    </Modal>
  );
};
